import type { FiscalNote } from '@/types'
import type { CustomerReceiptSettings } from '@/lib/printCustomerReceipt'
import { APP_ATTRIBUTION, dispatchCustomerReceiptPrint } from '@/lib/printCustomerReceipt'
import { FBR_DIGITAL_INVOICING_LOGO_URL } from '@/lib/fbrBranding'
import { PRA_LOGO_DATA_URI } from '@/lib/praBranding'

function escapeHtml(value: unknown): string {
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
}

function safeMoney(n: number): string {
  if (!Number.isFinite(n)) return '0.00'
  return n.toFixed(2)
}

/** Resolve a settings logo URL for the popup document. Leading-slash relative
 *  paths are made absolute against the current origin (mirrors the sales
 *  receipt) so they still load inside the blob window. Anything that isn't an
 *  http(s)/data URL is dropped. */
function resolveLogo(raw: string): string {
  const url = (raw ?? '').trim()
  if (!url) return ''
  if (url.startsWith('/') && typeof window !== 'undefined') {
    return `${window.location.origin}${url}`
  }
  return /^(https?:\/\/|data:image\/)/i.test(url) ? url : ''
}

/**
 * Builds the full printable HTML document for a customer-facing refund
 * receipt. Pure (no DOM/window access for http(s)/data logos) so it can be
 * unit-tested deterministically; the print dispatch lives in
 * {@link printRefundReceipt}.
 */
export function buildRefundReceiptHtml(
  note: FiscalNote,
  settings: CustomerReceiptSettings,
  opts?: { orderNumber?: string },
): string {
  const accent = settings.accentColor || '#111827'
  const createdAt = note.created_at ? new Date(note.created_at) : new Date()
  const createdLabel = createdAt.toLocaleString('en-GB')
  const orderNumber = (opts?.orderNumber ?? '').trim()

  // Fiscal compliance block only when this is a fiscalized refund with artifacts.
  const irn = note.fiscal_details?.irn?.trim() || ''
  const qrBase64 = (note.fiscal_details?.qr_code_png_base64 ?? '').replace(/\s/g, '')
  const hasQr = /^[A-Za-z0-9+/]+=*$/.test(qrBase64) && qrBase64.length > 80
  const showFiscal = !!note.fiscal_details && (!!irn || hasQr)

  // A credit note is a tax document: it must name the authority that actually
  // holds it. This block was hardcoded to FBR, so a PRA credit note printed
  // FBR's logo above a PRA invoice number and told the customer to verify with
  // the wrong regulator.
  const fiscalAuthority = String(note.fiscal_details?.authority ?? '').toUpperCase().trim()
  const isPraNote = fiscalAuthority === 'PRA_EIMS'
  const authorityLabel = isPraNote ? 'PRA' : 'FBR'
  const markMarkup = isPraNote
    ? `<div class="fiscal-mark"><img alt="Punjab Revenue Authority" src="${PRA_LOGO_DATA_URI}" /></div>`
    : `<div class="fiscal-mark"><img alt="FBR POS Invoicing System" src="${escapeHtml(FBR_DIGITAL_INVOICING_LOGO_URL)}" /></div>`
  const verifyHint = isPraNote
    ? 'Verify at the Punjab Revenue Authority e-IMS portal.'
    : 'Verify via FBR or authorized tax verification channels.'

  // ── Header (branded) ──────────────────────────────────────────────
  const logo = resolveLogo(settings.logoUrl)
  const logoMarkup = logo
    ? `<div class="logo-wrap"><img class="logo" alt="" src="${escapeHtml(logo)}" style="max-width:${Math.round(settings.logoWidthPercent || 75)}%" /></div>`
    : ''
  const addrLines = (settings.address || '')
    .split('\n')
    .map((l) => l.trim())
    .filter(Boolean)
    .map((l) => `<div>${escapeHtml(l)}</div>`)
    .join('')
  const contact: string[] = []
  if (settings.phone) contact.push(`<div>📞 ${escapeHtml(settings.phone)}</div>`)
  if (settings.email) contact.push(`<div>✉ ${escapeHtml(settings.email)}</div>`)
  if (settings.website) contact.push(`<div>🌐 ${escapeHtml(settings.website)}</div>`)
  const contactMarkup = contact.length ? `<div class="contact">${contact.join('')}</div>` : ''
  const legal: string[] = []
  if (settings.ntn) legal.push(`NTN / STRN: ${escapeHtml(settings.ntn)}`)
  if (settings.posNumber) legal.push(`POS # ${escapeHtml(settings.posNumber)}`)
  const legalMarkup = legal.length
    ? `<div class="legal">${legal.map((l) => `<div>${l}</div>`).join('')}</div>`
    : ''

  // ── Meta rows ─────────────────────────────────────────────────────
  const meta: Array<[string, string]> = []
  if (orderNumber) meta.push(['Order #', orderNumber])
  meta.push(['Refund date', createdLabel])
  if (note.created_by_name) meta.push(['Issued by', note.created_by_name])
  if (note.reason) meta.push(['Reason', note.reason])
  const metaMarkup = meta
    .map(([k, v]) => `<div class="kv"><span class="k">${escapeHtml(k)}</span><span class="v">${escapeHtml(v)}</span></div>`)
    .join('')

  // ── Refunded items (positive amounts) ─────────────────────────────
  const itemsHtml = note.items
    .map((it, idx) => {
      const qty = Number(it.quantity) || 0
      const unit = Number(it.unit_price) || 0
      const total = Number(it.total) || 0
      return `
        <tr>
          <td class="idx">${idx + 1}</td>
          <td class="desc">
            <div class="name">${escapeHtml(it.product_name)}</div>
            <div class="sub muted">${escapeHtml(String(qty))} × ${escapeHtml(safeMoney(unit))}</div>
          </td>
          <td class="num">${escapeHtml(safeMoney(total))}</td>
        </tr>`
    })
    .join('')

  // ── Fiscal compliance footer ──────────────────────────────────────
  const fbrMarkup = showFiscal
    ? `
    <div class="dash"></div>
    <section class="fbr">
      ${markMarkup}
      ${hasQr ? `<div class="qr"><img alt="${authorityLabel} verification QR" src="data:image/png;base64,${qrBase64}" /></div>` : ''}
      ${irn ? `<div class="fbr-irn"><div class="fbr-irn-lbl">${authorityLabel} credit note / ${isPraNote ? 'invoice no.' : 'IRN'}</div><div class="fbr-irn-val">${escapeHtml(irn)}</div></div>` : ''}
      ${note.ref_invoice_number ? `<div class="kv"><span class="k">Original invoice</span><span class="v">${escapeHtml(note.ref_invoice_number)}</span></div>` : ''}
      <div class="fbr-hint muted">${escapeHtml(verifyHint)}</div>
    </section>`
    : ''

  const refundFooter = (settings.refundFooterMessage || '').trim()

  const html = `<!doctype html>
<html lang="en">
<head>
  <meta charset="utf-8" />
  <title>Refund Receipt ${escapeHtml(orderNumber || irn || note.id)}</title>
  <style>
    :root { color-scheme: light; --accent: ${escapeHtml(accent)}; }
    * { box-sizing: border-box; }
    body { margin: 0; padding: 0; background: #fff; }
    .paper {
      font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
      font-size: 12px;
      line-height: 1.3;
      color: #111;
      width: 76mm;
      margin: 0 auto;
      padding: 10px 10px 12px;
    }
    .center { text-align: center; }
    .muted { color: #6b7280; }
    .dash { border-top: 1px dashed #cbd5e1; margin: 10px 0; }
    .head { text-align: center; }
    .logo-wrap { margin-bottom: 6px; }
    .logo { display: block; margin: 0 auto; max-height: 56px; object-fit: contain; }
    .brand { font-size: 15px; font-weight: 800; letter-spacing: 0.02em; color: var(--accent); }
    .addr, .contact, .legal { font-size: 10px; margin-top: 3px; color: #374151; }
    .title-bar { height: 2px; background: var(--accent); margin: 10px 0 6px; opacity: 0.9; }
    h1 { font-size: 14px; margin: 0; letter-spacing: 0.1em; text-align: center; }
    table { width: 100%; border-collapse: collapse; }
    td { vertical-align: top; padding: 4px 0; }
    .idx { width: 14px; padding-right: 6px; color: #6b7280; }
    .desc { padding-right: 8px; }
    .name { white-space: nowrap; overflow: hidden; text-overflow: ellipsis; max-width: 44mm; }
    .sub { font-size: 10px; margin-top: 2px; }
    .num { text-align: right; font-variant-numeric: tabular-nums; white-space: nowrap; }
    .kv { display: flex; justify-content: space-between; gap: 10px; padding: 2px 0; }
    .kv .k { color: #6b7280; }
    .kv .v { text-align: right; word-break: break-word; }
    .total { font-size: 14px; font-weight: 800; }
    .fbr { text-align: center; }
    .fbr-mark img, .fiscal-mark img { height: 40px; width: auto; object-fit: contain; display: block; margin: 0 auto 6px; }
    /* 48mm was almost the full printable width of a 58mm roll. 26mm still gives
       ~7px per module for this symbol at 203dpi — far above the 7x7mm FBR
       mandates — while leaving the refund detail as the subject of the slip. */
    .qr img { display: block; margin: 4px auto; width: 26mm; height: 26mm; image-rendering: pixelated; }
    .fbr-irn-lbl { font-size: 9px; color: #6b7280; text-transform: uppercase; letter-spacing: 0.06em; }
    .fbr-irn-val { font-weight: 800; word-break: break-all; font-size: 11px; }
    .fbr-hint { font-size: 9px; margin-top: 4px; }
    .policy { font-size: 10px; text-align: center; margin-top: 8px; }
    .footer { font-size: 10px; margin-top: 8px; text-align: center; }
    .attr { font-size: 9px; color: #6b7280; margin-top: 6px; text-align: center; }
    @media print {
      @page { margin: 0; }
      .paper { width: auto; }
    }
  </style>
</head>
<body>
  <div class="paper">
    <div class="head">
      ${logoMarkup}
      <div class="brand">${escapeHtml(settings.businessName || 'Restaurant')}</div>
      ${addrLines ? `<div class="addr">${addrLines}</div>` : ''}
      ${contactMarkup}
      ${legalMarkup}
    </div>

    <div class="title-bar"></div>
    <h1>REFUND RECEIPT</h1>
    <div class="center muted" style="font-size:10px;margin-top:2px;">Generated ${escapeHtml(createdLabel)}</div>

    <div class="dash"></div>
    ${metaMarkup}

    <div class="dash"></div>
    <div class="center muted" style="font-size:10px;margin-bottom:6px;">REFUNDED ITEMS</div>
    <table>${itemsHtml}</table>

    <div class="dash"></div>
    <div class="kv"><span class="k">Subtotal refunded</span><span class="num">${escapeHtml(safeMoney(note.subtotal))}</span></div>
    ${note.tax_amount > 0 ? `<div class="kv"><span class="k">Sales tax refunded</span><span class="num">${escapeHtml(safeMoney(note.tax_amount))}</span></div>` : ''}
    <div class="kv total"><span>Total Refunded</span><span class="num">${escapeHtml(safeMoney(note.total_amount))}</span></div>

    ${fbrMarkup}

    <div class="dash"></div>
    ${refundFooter ? `<div class="policy">${escapeHtml(refundFooter)}</div>` : ''}
    <div class="footer">${escapeHtml(settings.businessName || 'Restaurant')}</div>
    <div class="attr">${escapeHtml(APP_ATTRIBUTION)}</div>
  </div>
</body>
</html>`

  return html
}

/**
 * Customer-facing refund receipt — a branded proof-of-refund handed to the
 * guest, distinct from the internal/fiscal credit note (printFbrCreditNote).
 * Reuses the same receipt branding (logo, business name, contact, accent) and
 * carries the FBR credit-note QR + IRN as verifiable proof the tax was
 * reversed. Refund amounts are shown as positive totals (not red negatives) —
 * guest-friendly — with no payment-method breakdown.
 *
 * Prints via the proven off-screen-iframe path (no popup window, which some
 * browsers reroute to a new tab / search bar). paperWidthMm drives the
 * exact-page-size lock so the thermal driver doesn't pad to its roll preset.
 */
export function printRefundReceipt(
  note: FiscalNote,
  settings: CustomerReceiptSettings,
  opts?: { orderNumber?: string },
): { printed: boolean } {
  const html = buildRefundReceiptHtml(note, settings, opts)
  return { printed: dispatchCustomerReceiptPrint(html, settings.paperWidthMm) }
}
